import React, { useCallback, useContext, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import CenterLayout from '../layout/CenterLayout';
import AppContext from '../Context/AppContext';
import { useSocket } from '../Context/SocketContext';
import Loader from '../components/Loader';
import { Toast } from '../components/userProject/UpUi';
import ProjectMethods, { Switch } from '../components/paymentSettings/ProjectMethods';
import { PROJECTS, RULES, readConfig, setValue } from '../components/paymentSettings/espremium';

const euro = (n) => Number(n).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })

/**
 * Paramètres paiement : tout ce qui règle les paiements, au même endroit (avant : des widgets sur le côté des pages
 * Paiement et Messages). Les réglages sont chez ESPREMIUM, c'est lui qui décide : on les lit et on les modifie par son socket.
 *  - Projets : paiement ouvert ou fermé, moyens proposés et leur ordre, compte Stripe utilisé (PrimeStation a le sien)
 *  - Règles communes : paiement automatique, moyens manuels, admin disponible
 *  - Tarifs : lecture seule (PRICEEMPIRE dans project.json d'ESPREMIUM)
 */
const PaymentSettings = () => {
    const { setCurrentPage } = useContext(AppContext)
    const { socket, getJSON, setPriceEmpire } = useSocket()
    const [config, setConfig] = useState(null)
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(null)
    const [toast, setToast] = useState(null)

    useEffect(() => { setCurrentPage('parametres_paiement') }, [setCurrentPage])

    const load = useCallback(async () => {
        setError('')
        try { setConfig(await readConfig(socket)) } catch (e) { setError(e.message) }
    }, [socket])

    // lecture dès que le socket ESPREMIUM est connecté (et à chaque reconnexion)
    useEffect(() => {
        if (!socket) return
        const failed = () => setError("ESPREMIUM injoignable : vérifie REACT_APP_PUBLIC_SOCKET_URL et qu'il tourne")
        if (socket.connected) load()
        socket.on('connect', load)
        socket.on('connect_error', failed)
        return () => {
            socket.off('connect', load)
            socket.off('connect_error', failed)
        }
    }, [socket, load])

    // enregistre, relit chez ESPREMIUM, et garde le reste du tracker à jour
    const save = useCallback(async (key, action, done) => {
        setBusy(key)
        try {
            await action(socket)
            setConfig(await readConfig(socket))
            getJSON(socket)
            socket.emit('empire_price', {}, (e) => setPriceEmpire(e))
            setToast({ text: done, at: Date.now() })
        } catch (e) {
            setToast({ text: e.message, error: true, at: Date.now() })
        }
        setBusy(null)
    }, [socket, getJSON, setPriceEmpire])

    const onToastDone = useCallback(() => setToast(null), [])

    return (
        <MainLayout>
            <CenterLayout>
                <div className='up-page pset'>
                    <header className='up-head'>
                        <div>
                            <h1>Paramètres paiement</h1>
                            <p className='up-muted'>Enregistrés chez ESPREMIUM : c'est lui qui décide, les projets lisent ces réglages chez lui.</p>
                        </div>
                        {config && <button className='btn cw up-icon-btn' onClick={load} title='Relire chez ESPREMIUM'><RefreshCw size={15} /></button>}
                    </header>

                    {error && <p className='up-error'>{error}</p>}
                    {!config && !error && <div className='up-loading'><Loader /></div>}

                    {config && (
                        <>
                            <section className='pset-section'>
                                <h2>Projets</h2>
                                <p className='up-muted'>Chaque projet a son interrupteur et ses propres moyens de paiement, proposés aux users dans cet ordre.</p>
                                <div className='pset-projects'>
                                    {PROJECTS.map((p) => <ProjectMethods key={p.id} project={p} config={config} busy={busy} save={save} />)}
                                </div>
                            </section>

                            <section className='pset-section'>
                                <h2>Règles communes</h2>
                                <p className='up-muted'>Pour toutes les demandes de paiement, quel que soit le projet.</p>
                                <div className='pset-rules'>
                                    {RULES.map((r) => (
                                        <div key={r.field} className='pset-rule'>
                                            <div>
                                                <b>{r.label}</b>
                                                <small>{r.hint}</small>
                                            </div>
                                            <Switch
                                                on={Boolean(config[r.field])}
                                                disabled={Boolean(busy)}
                                                label={r.label}
                                                onChange={(on) => save(`rule:${r.field}`, (s) => setValue(s, r.field, on), `${r.label} : ${on ? 'activé' : 'coupé'}`)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </section>

                            <section className='pset-section'>
                                <h2>Tarifs</h2>
                                <p className='up-muted'>Communs à tous les projets · lecture seule (PRICEEMPIRE dans project.json d'ESPREMIUM).</p>
                                <div className='pset-prices'>
                                    {(config.PRICEEMPIRE || []).map((pr) => (
                                        <div key={pr.id} className='up-card'>
                                            <span className='up-card-label'>{pr.title}</span>
                                            <span className='up-card-value'>{euro(pr.discount ? pr.price * (1 - pr.discount / 100) : pr.price)}</span>
                                            {pr.discount > 0 && <small className='up-muted'>au lieu de {euro(pr.price)} (-{pr.discount} %)</small>}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </>
                    )}
                    <Toast toast={toast} onDone={onToastDone} />
                </div>
            </CenterLayout>
        </MainLayout>
    );
};

export default PaymentSettings;
